import { useState, useEffect } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money, Drawer, ConfirmDialog } from '../components/SharedComponents'

/* ============ CoCafe Host — Bookings ============ */
function ChannelTag({ ch }) {
  return <span className="t-xs muted row g4">{ch === "app" ? <HI.message s={12} /> : <HI.external s={12} />}{ch === "app" ? "App" : "Web"}</span>;
}

function BookingDrawer({ booking, onClose, toast, onCancel, onModify }) {
  const b = booking;
  const within = b.dayOffset >= 0; // upcoming/today
  const total = b.amount;
  const fee = Math.round(total * 0.05);
  const sessionRate = total - 0; // dummy
  const timeline = [
    { t: "Booked", when: b.dayOffset <= 0 ? "2 days ago via " + (b.channel === "app" ? "the app" : "web") : "Yesterday", done: true },
    { t: "Payment confirmed", when: "Instantly · " + money(total), done: true },
    { t: "Reminder sent", when: b.dayOffset >= 0 ? "Scheduled · 1h before" : "Sent", done: b.dayOffset < 0 },
    { t: b.status === "checked-in" ? "Checked in" : b.status === "completed" ? "Completed" : "Check-in", when: b.status === "checked-in" ? "Today, " + H.hLabel(b.start) : b.status === "completed" ? "Done" : "Pending", done: b.status === "checked-in" || b.status === "completed" },
  ];

  return (
    <Drawer title={b.guest} sub={"Booking " + b.id} onClose={onClose}
      footer={
        b.status === "upcoming" ? (
          <>
            <button className="btn btn-danger grow" onClick={() => onCancel(b)}>Cancel booking</button>
            <button className="btn btn-ghost grow" onClick={() => onModify(b)}>Modify</button>
            <button className="btn btn-pri grow" onClick={() => toast("Message sent to " + b.guest)}>Message guest</button>
          </>
        ) : b.status === "checked-in" ? (
          <>
            <button className="btn btn-ghost grow" onClick={() => toast("Receipt sent")}>Send receipt</button>
            <button className="btn btn-pri grow" onClick={() => toast(b.guest + " marked as departed")}>Mark departed</button>
          </>
        ) : (
          <button className="btn btn-ghost btn-block" onClick={() => toast("Rebooking link sent to " + b.guest)}>Invite to rebook</button>
        )
      }>
      {/* status */}
      <div className="row between" style={{ marginBottom: 16 }}>
        <StatusBadge status={b.status} />
        <ChannelTag ch={b.channel} />
      </div>

      {/* guest card */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row g8" style={{ alignItems: 'center' }}>
          <Av name={b.guest} initials={b.initials} c={b.c} size="sm" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="t-sm" style={{ fontWeight: 700, marginBottom: 2 }}>{b.guest}</div>
            <div className="t-xs muted">{b.guest === "Aarav Menon" ? "Regular · 11 visits" : b.guest === "Divya Nair" ? "First-time guest" : "3 visits"} · joined 2025</div>
          </div>
          <button className="btn btn-soft btn-sm" onClick={() => toast("Opening guest profile")}><HI.user s={14} /></button>
        </div>
      </div>

      {/* requests */}
      {b.requests.length > 0 && (
        <Banner tone="blue" icon={<HI.info s={17} />} title="Special requests">
          <div className="row g6 wrap" style={{ marginTop: 6 }}>
            {b.requests.map((r) => <span key={r} className="chip" style={{ background: "#fff" }}>{r}</span>)}
          </div>
        </Banner>
      )}
      {b.note && <div className="t-sm" style={{ marginTop: 12, padding: "10px 12px", background: "var(--surface-2)", borderRadius: "var(--r)", color: "var(--ink-2)" }}><HI.message s={14} style={{ verticalAlign: -2, marginRight: 6, color: "var(--muted)" }} />{b.note}</div>}

      {/* details */}
      <div className="t-eyebrow" style={{ margin: "22px 0 10px" }}>Session</div>
      <div className="col g10">
        {[
          [<HI.calendar s={15} />, "Date", b.dateLabel],
          [<HI.clock s={15} />, "Time", H.hLabel(b.start) + " – " + H.hLabel(b.end) + " (" + b.dur + ")"],
          [<HI.seat s={15} />, "Desks", b.seats + (b.seats > 1 ? " desks" : " desk")],
          [<HI.coffee s={15} />, "Included", b.seats + "× filter coffee"],
        ].map(([ic, k, v], i) => (
          <div key={i} className="row between">
            <span className="row g8 muted t-sm">{ic}{k}</span>
            <span className="t-sm" style={{ fontWeight: 600 }}>{v}</span>
          </div>
        ))}
      </div>

      {/* price */}
      <div className="t-eyebrow" style={{ margin: "22px 0 10px" }}>Payment</div>
      <div className="card card-pad" style={{ background: "var(--surface-2)" }}>
        <div className="row between t-sm" style={{ marginBottom: 7 }}><span className="muted">Session ({b.dur} × {b.seats})</span><span className="mono">{money(sessionRate)}</span></div>
        <div className="row between t-sm" style={{ marginBottom: 7 }}><span className="muted">CoCafe service fee</span><span className="mono muted">−{money(fee)}</span></div>
        <hr className="hr" style={{ margin: "9px 0" }} />
        <div className="row between"><span style={{ fontWeight: 700 }}>Your payout</span><span className="mono" style={{ fontWeight: 700, fontSize: 16 }}>{money(total - fee)}</span></div>
      </div>

      {/* timeline */}
      <div className="t-eyebrow" style={{ margin: "22px 0 10px" }}>Timeline</div>
      <div className="col" style={{ gap: 0 }}>
        {timeline.map((s, i) => (
          <div key={i} className="row g10" style={{ alignItems: "flex-start" }}>
            <div className="col center" style={{ width: 18 }}>
              <span style={{ width: 11, height: 11, borderRadius: "50%", background: s.done ? "var(--green)" : "var(--surface)", border: "2px solid " + (s.done ? "var(--green)" : "var(--border-3)"), flexShrink: 0 }} />
              {i < timeline.length - 1 && <span style={{ width: 2, flex: 1, minHeight: 24, background: s.done ? "var(--green)" : "var(--border)" }} />}
            </div>
            <div style={{ paddingBottom: 14 }}>
              <div className="t-sm" style={{ fontWeight: 600 }}>{s.t}</div>
              <div className="t-xs muted">{s.when}</div>
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}

function CancelDialog({ booking, onClose, onConfirm }) {
  const b = booking;
  // refund policy: free cancellation up to 1h before start; today bookings near start = partial
  const freeWindow = b.dayOffset > 0; // future days = inside free window
  const refund = freeWindow ? b.amount : Math.round(b.amount * 0.5);
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="scrim" onClick={onClose} />
      <div className="modal-card">
        <div className="modal-b">
          <div className="t-h2" style={{ marginBottom: 8 }}>Cancel {b.id}?</div>
          <div className="t-body" style={{ lineHeight: 1.55, marginBottom: 14 }}>
            You're cancelling <strong>{b.guest}</strong>'s {b.dur} booking on {b.dateLabel}. They'll be notified right away.
          </div>
          <Banner tone={freeWindow ? "amber" : "red"} icon={<HI.info s={17} />} title={freeWindow ? "Inside free-cancellation window" : "Late cancellation"}>
            {freeWindow
              ? <>This booking starts more than 1 hour out, so the guest receives a <strong>full {money(refund)} refund</strong>. Frequent host cancellations can affect your Superhost status.</>
              : <>Less than 1 hour to start — guest is refunded <strong>{money(refund)} (50%)</strong> per your policy. Consider messaging them first.</>}
          </Banner>
          <div className="field" style={{ marginTop: 14 }}>
            <span className="lbl">Reason (shared with the guest)</span>
            <select className="sel"><option>Space unexpectedly unavailable</option><option>Maintenance issue</option><option>Double-booking</option><option>Other</option></select>
          </div>
        </div>
        <div className="modal-f">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Keep booking</button>
          <button className="btn btn-danger" disabled={busy} onClick={() => { setBusy(true); setTimeout(() => onConfirm(b, refund), 650); }}>
            {busy ? "Cancelling…" : "Cancel & refund " + money(refund)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Bookings({ toast, openId, clearOpen }) {
  const [tab, setTab] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState(H.bookings);
  const [active, setActive] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [query, setQuery] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { const t = setTimeout(() => setLoading(false), 650); return () => clearTimeout(t); }, []);
  useEffect(() => { if (openId) { const b = rows.find((r) => r.id === openId); if (b) setActive(b); clearOpen && clearOpen(); } }, [openId]);

  const isUpcoming = (b) => ["upcoming", "checked-in", "web"].includes(b.status) && b.dayOffset >= 0;
  const isPast = (b) => ["completed", "cancelled", "no-show"].includes(b.status);

  let list = rows.filter((b) => (tab === "upcoming" ? isUpcoming(b) : tab === "past" ? isPast(b) : true));

  // Apply search filter
  if (searchText.trim()) {
    list = list.filter((b) =>
      (b.guest.toLowerCase().includes(searchText.toLowerCase()) ||
       b.id.toLowerCase().includes(searchText.toLowerCase()))
    );
  }

  // Apply status filter
  if (statusFilter !== "all") {
    list = list.filter((b) => b.status === statusFilter);
  }

  list = [...list].sort((a, b) => a.dayOffset - b.dayOffset || a.start - b.start);

  // Pagination
  const totalPages = Math.ceil(list.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedList = list.slice(startIdx, startIdx + itemsPerPage);

  const counts = {
    upcoming: rows.filter(isUpcoming).length,
    past: rows.filter(isPast).length,
    all: rows.length,
  };

  const doCancel = (b, refund) => {
    setRows((r) => r.map((x) => x.id === b.id ? { ...x, status: "cancelled", note: "Cancelled by host — " + money(refund) + " refunded." } : x));
    setCancelling(null); setActive(null);
    toast("Booking cancelled", b.guest + " refunded " + money(refund));
  };

  return (
    <div className="page fade-in">

      {/* tabs */}
      <div className="row between wrap g12" style={{ marginBottom: 16 }}>
        <div className="tabs" style={{ border: 0 }}>
          {[["upcoming", "Upcoming"], ["past", "Past"], ["all", "All"]].map(([k, l]) => (
            <button key={k} className={"tab" + (tab === k ? " on" : "")} onClick={() => { setTab(k); setCurrentPage(1); }}>{l}<span className="cnt">{counts[k]}</span></button>
          ))}
        </div>
      </div>

      {/* Search and filters */}
      <div className="row g12" style={{ marginBottom: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <input
            type="text"
            className="inp"
            placeholder="Search by guest name or booking ID..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
            style={{ width: '100%' }}
            aria-label="Search bookings"
          />
        </div>
        <select
          className="sel"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          aria-label="Filter by status"
          style={{ minWidth: 150 }}
        >
          <option value="all">All Statuses</option>
          <option value="upcoming">Upcoming</option>
          <option value="checked-in">Checked In</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="no-show">No-show</option>
        </select>
      </div>

      {/* Results counter */}
      <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--muted)' }}>
        {searchText || statusFilter !== 'all' ? (
          <>{list.length} booking{list.length !== 1 ? 's' : ''} found</>
        ) : (
          <>{list.length} booking{list.length !== 1 ? 's' : ''}</>
        )}
      </div>

      {/* TABLE: Desktop view - hidden on mobile with CSS */}
      <div className="bookings-table-container">
        {loading ? <SkeletonRows rows={6} cols={5} /> : list.length === 0 ? (
          <Empty icon={<HI.calendar s={22} />} title="No bookings found"
            desc={searchText || statusFilter !== 'all' ? "No bookings match your search or filters. Try adjusting your criteria." : tab === "upcoming" ? "When guests book a desk, they'll appear here. Sharpen your pricing or open more evening slots to fill the week." : "Your booking history will build up here."}
            action={tab === "upcoming" ? <button className="btn btn-pri btn-sm" onClick={() => toast("Opening pricing")}>Review pricing</button> : null} />
        ) : (
          <>
            <table className="tbl">
              <thead><tr>
                <th>Guest</th><th>Date & time</th><th>Session</th><th>Desks</th><th>Channel</th><th className="num">Amount</th><th>Status</th>
              </tr></thead>
              <tbody>
                {paginatedList.map((b) => (
                  <tr key={b.id} onClick={() => setActive(b)}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Av name={b.guest} initials={b.initials} c={b.c} size="sm" />
                          <div style={{ fontSize: '12px', fontWeight: 650 }}>{b.guest}</div>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--muted)', fontFamily: 'monospace', paddingLeft: '32px' }}>{b.id}</div>
                      </div>
                    </td>
                    <td><div style={{ fontWeight: 600 }}>{b.dateLabel}</div><div className="t-xs muted">{H.hLabel(b.start)}-{H.hLabel(b.end)}</div></td>
                    <td><span className="badge gray" style={{ fontWeight: 600 }}>{b.dur}</span></td>
                    <td className="muted">{b.seats}</td>
                    <td><ChannelTag ch={b.channel} /></td>
                    <td className="num">{money(b.amount)}</td>
                    <td><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  aria-label="Previous page"
                >
                  ← Previous
                </button>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* CARDS: Mobile view - shown on mobile with CSS */}
      {!loading && list.length > 0 && (
        <div className="bookings-cards-container">
          {paginatedList.map((b) => (
            <div key={b.id} className="bookings-card card" style={{ marginBottom: 16, cursor: "pointer" }} onClick={() => setActive(b)}>
              <div className="card-pad" style={{ paddingBottom: 14 }}>
                <div className="row g10" style={{ marginBottom: 14, alignItems: "center" }}>
                  <Av name={b.guest} initials={b.initials} c={b.c} size="sm" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-sm" style={{ fontWeight: 700 }}>{b.guest}</div>
                    <div className="t-xs muted">{b.id}</div>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
              </div>
              <div className="card-pad" style={{ paddingTop: 0 }}>
                <div className="bookings-card-row">
                  <span className="label">Date</span>
                  <span className="value" style={{ fontWeight: 600 }}>{b.dateLabel}</span>
                </div>
                <div className="bookings-card-row">
                  <span className="label">Time</span>
                  <span className="value">{H.hLabel(b.start)}-{H.hLabel(b.end)}</span>
                </div>
                <div className="bookings-card-row">
                  <span className="label">Session</span>
                  <span className="value">{b.dur}</span>
                </div>
                <div className="bookings-card-row">
                  <span className="label">Desks</span>
                  <span className="value">{b.seats}</span>
                </div>
                <div className="bookings-card-row">
                  <span className="label">Channel</span>
                  <span className="value"><ChannelTag ch={b.channel} /></span>
                </div>
                <div className="bookings-card-row" style={{ borderBottom: "none" }}>
                  <span className="label">Amount</span>
                  <span className="value" style={{ fontWeight: 700, color: "var(--green)" }}>{money(b.amount)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && list.length === 0 && (
        <Empty icon={<HI.calendar s={22} />} title={tab === "upcoming" ? "No upcoming bookings" : tab === "past" ? "No past bookings yet" : "No bookings found"}
          desc={tab === "upcoming" ? "When guests book a desk, they'll appear here. Sharpen your pricing or open more evening slots to fill the week." : query ? "Try a different name or booking ID." : "Your booking history will build up here."}
          action={tab === "upcoming" ? <button className="btn btn-pri btn-sm" onClick={() => toast("Opening pricing")}>Review pricing</button> : null} />
      )}

      {!loading && list.length > 0 && (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <span className="t-xs muted">Showing {list.length} of {counts.all} bookings</span>
        </div>
      )}

      {active && <BookingDrawer booking={active} onClose={() => setActive(null)} toast={toast}
        onCancel={(b) => setCancelling(b)} onModify={(b) => toast("Modify booking", "Reschedule flow for " + b.id)} />}
      {cancelling && <CancelDialog booking={cancelling} onClose={() => setCancelling(null)} onConfirm={doCancel} />}
    </div>
  );
}

export default Bookings;

